import React, { useEffect, useState, useRef } from 'react';
import { X, EnvelopeSimple, Check } from '@phosphor-icons/react';
import { Spinner } from '../ui/Spinner';
import { cn } from '../../lib/utils';
interface VerifyEmailModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onSuccess: () => void;
}
type Phase = 'enter' | 'verifying' | 'success';
const OTP_LENGTH = 6;
const COOLDOWN_SECONDS = 30;
export function VerifyEmailModal({
  open,
  email,
  onClose,
  onSuccess
}: VerifyEmailModalProps) {
  const [phase, setPhase] = useState<Phase>('enter');
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPhase('enter');
      setDigits(Array(OTP_LENGTH).fill(''));
      setError(null);
      setCooldown(COOLDOWN_SECONDS);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    }
  }, [open]);
  // Cooldown ticker
  useEffect(() => {
    if (!open || cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [open, cooldown]);
  // Escape to close
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'verifying') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, phase, onClose]);
  // Lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  if (!open) return null;
  const code = digits.join('');
  const isComplete = code.length === OTP_LENGTH && digits.every(d => d !== '');
  const handleChange = (idx: number, val: string) => {
    const sanitized = val.replace(/\D/g, '').slice(-1);
    setDigits(curr => {
      const next = [...curr];
      next[idx] = sanitized;
      return next;
    });
    setError(null);
    if (sanitized && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };
  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const lastIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[lastIdx]?.focus();
  };
  const handleVerify = async () => {
    if (!isComplete) return;
    setPhase('verifying');
    await new Promise(r => setTimeout(r, 1200));
    // Mock: accept any 6-digit code; reject "000000"
    if (code === '000000') {
      setPhase('enter');
      setError('Cod incorect. Verifică emailul și încearcă din nou.');
      return;
    }
    setPhase('success');
    await new Promise(r => setTimeout(r, 1200));
    onSuccess();
  };
  const handleResend = () => {
    if (cooldown > 0) return;
    setCooldown(COOLDOWN_SECONDS);
    setDigits(Array(OTP_LENGTH).fill(''));
    setError(null);
    inputsRef.current[0]?.focus();
  };
  return <div role="dialog" aria-modal="true" aria-label="Verifică email" className="fixed inset-0 z-50 bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={() => phase !== 'verifying' && onClose()} data-id="element-1883">
      <div className="w-full sm:max-w-md bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1884">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-3" data-id="element-1885">
          <h2 className="text-base font-semibold text-light-1" data-id="element-1886">
            Verifică email
          </h2>
          <button type="button" onClick={onClose} disabled={phase === 'verifying'} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer disabled:opacity-50" data-id="element-1887">
            <X size={18} weight="bold" data-id="element-1888" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-6 sm:py-8" data-id="element-1889">
          {phase === 'success' ? <div className="flex flex-col items-center text-center py-4" data-id="element-1890">
              <div className="w-16 h-16 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-4" data-id="element-1891">
                <Check size={32} weight="bold" className="text-lime-1" data-id="element-1892" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-1893">
                Email verificat
              </h3>
              <p className="text-sm text-light-3" data-id="element-1894">
                Badge-ul apare acum pe profilul tău.
              </p>
            </div> : <>
              <div className="flex flex-col items-center text-center mb-6" data-id="element-1895">
                <div className="w-14 h-14 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-3" data-id="element-1896">
                  <EnvelopeSimple size={26} weight="regular" className="text-lime-1" data-id="element-1897" />
                </div>
                <p className="text-sm text-light-2" data-id="element-1898">
                  Am trimis un cod de 6 cifre la
                </p>
                <p className="text-sm font-semibold text-light-1 mt-0.5" data-id="element-1899">
                  {email}
                </p>
              </div>

              {/* OTP inputs */}
              <div className="flex justify-center gap-2 sm:gap-3 mb-3" data-id="element-1900">
                {digits.map((d, i) => <input key={i} ref={el => {
              inputsRef.current[i] = el;
            }} type="text" inputMode="numeric" maxLength={1} value={d} disabled={phase === 'verifying'} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste} aria-label={`Cifra ${i + 1}`} className={cn('w-11 h-13 sm:w-12 sm:h-14 rounded-md bg-dark-1 border text-center text-xl font-semibold text-light-1 transition-colors', 'focus:outline-none focus:border-lime-1', error ? 'border-red-2' : 'border-dark-4', phase === 'verifying' && 'opacity-50')} data-id="element-1901" />)}
              </div>

              {error && <p className="text-xs text-red-1 text-center mb-3" data-id="element-1902">{error}</p>}

              {/* Resend */}
              <div className="flex items-center justify-center text-xs text-light-3 mb-6" data-id="element-1903">
                <span data-id="element-1904">Nu ai primit codul?</span>
                <button type="button" onClick={handleResend} disabled={cooldown > 0 || phase === 'verifying'} className={cn('ml-1.5 font-semibold transition-colors', cooldown > 0 ? 'text-light-4 cursor-not-allowed' : 'text-lime-1 hover:text-lime-2 cursor-pointer')} data-id="element-1905">
                  {cooldown > 0 ? `Retrimite (${cooldown}s)` : 'Retrimite codul'}
                </button>
              </div>

              {/* CTA */}
              <button type="button" onClick={handleVerify} disabled={!isComplete || phase === 'verifying'} className={cn('w-full inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors', isComplete && phase !== 'verifying' ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-1906">
                {phase === 'verifying' ? <>
                    <Spinner size="sm" data-id="element-1907" />
                    Se verifică...
                  </> : 'Verifică'}
              </button>
            </>}
        </div>
      </div>
    </div>;
}